// Patch Tool (J) — selects an area, then drags it to a source area to blend.
// Uses the same texture-blending algorithm as the Healing Brush.
//
// Workflow (matches Photoshop's Patch tool):
// 1. Click and drag to create a selection (rectangular for MVP).
// 2. Release — the selection is now "armed" and stays fixed on screen.
// 3. Press down *inside* the armed selection and drag it to a source area;
//    a ghost preview follows the pointer while the button is held.
// 4. Release the button to drop it — the healing blend commits at that point.
//
// Note: step 3 is a genuine press-drag-release, not a second click-to-place
// — see audit finding 79 (the previous version fired the heal on the second
// pointerDOWN instead of pointerUP, so there was no live drag preview).

import { HealStrokeCommand, snapshotRegion } from "../commands/HealStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import type { BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export class PatchTool implements Tool {
  readonly id = "patch";
  readonly shortcut = "J";

  // idle: nothing going on.
  // selecting: first drag (mouse held), sizing the rectangular selection.
  // armed: selection finalized (mouse released); waiting for the user to
  //        press down inside it to begin the actual patch drag.
  // dragging: mouse held, dragging the armed selection toward a source area;
  //        dragOffset updates live for the ghost-preview, and releasing the
  //        button commits the heal.
  private state: "idle" | "selecting" | "armed" | "dragging" = "idle";
  private selection: { x0: number; y0: number; x1: number; y1: number } | null = null;
  private dragAnchor: { x: number; y: number } | null = null;
  private dragOffset: { dx: number; dy: number } | null = null;
  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    selBBox: BBox;
    destOffset: { dx: number; dy: number };
  } | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;

    if (this.state === "idle") {
      if (!ctx.doc.activeLayerId) return;
      const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
      if (!layer || !layer.visible) return;
      if (layer.lockAll || layer.lockImage) {
        ctx.showTip("Layer is locked.");
        return;
      }

      const p = screenToDoc(ctx.viewport, s.x, s.y);
      this.selection = { x0: p.x, y0: p.y, x1: p.x, y1: p.y };
      this.state = "selecting";
      ctx.requestRender();
      return;
    }

    if (this.state === "armed" && this.selection && this.active) {
      // Press down to arm the actual drag. The anchor is where the button
      // went down; dragOffset (used for the ghost preview and the final
      // heal) is computed relative to it as the pointer moves.
      const p = screenToDoc(ctx.viewport, s.x, s.y);
      this.dragAnchor = { x: p.x, y: p.y };
      this.dragOffset = { dx: 0, dy: 0 };
      this.state = "dragging";
      ctx.requestRender();
      return;
    }
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);

    if (this.state === "selecting" && this.selection) {
      this.selection.x1 = p.x;
      this.selection.y1 = p.y;
      ctx.requestRender();
      return;
    }

    if (this.state === "dragging" && this.dragAnchor) {
      this.dragOffset = {
        dx: p.x - this.dragAnchor.x,
        dy: p.y - this.dragAnchor.y,
      };
      ctx.requestRender();
    }
  }

  onPointerUp(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;

    if (this.state === "selecting" && this.selection) {
      const selW = Math.abs(this.selection.x1 - this.selection.x0);
      const selH = Math.abs(this.selection.y1 - this.selection.y0);
      if (selW < 2 || selH < 2) {
        this.reset();
        ctx.requestRender();
        return;
      }

      if (!ctx.doc.activeLayerId) { this.reset(); return; }
      const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
      if (!layer || !layer.visible) { this.reset(); return; }

      const x0 = Math.floor(Math.min(this.selection.x0, this.selection.x1));
      const y0 = Math.floor(Math.min(this.selection.y0, this.selection.y1));
      const x1 = Math.ceil(Math.max(this.selection.x0, this.selection.x1));
      const y1 = Math.ceil(Math.max(this.selection.y0, this.selection.y1));

      this.active = {
        layer,
        layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
        selBBox: { x: x0, y: y0, w: x1 - x0, h: y1 - y0 },
        destOffset: { dx: 0, dy: 0 },
      };

      // Selection is now armed — fixed on screen until the user presses
      // down inside it (onPointerDown, "armed" branch above) to start the
      // actual drag-to-source gesture.
      this.state = "armed";
      ctx.requestRender();
      return;
    }

    if (this.state === "dragging" && this.active) {
      // Release commits the heal at the current drag offset — this is the
      // point where the drag-and-release gesture actually lands, unlike the
      // old click-to-place behavior.
      this.active.destOffset = this.dragOffset ?? { dx: 0, dy: 0 };
      this.applyHeal(ctx);
      this.reset();
      ctx.requestRender();
    }
  }

  private applyHeal(ctx: ToolContext): void {
    if (!this.active) return;
    const { layer, layerSnapshot, selBBox, destOffset } = this.active;
    const data = layer.pixels.data;
    const srcData = layerSnapshot;
    const w = layer.pixels.width;
    const h = layer.pixels.height;

    const srcX0 = selBBox.x;
    const srcY0 = selBBox.y;
    const srcW = selBBox.w;
    const srcH = selBBox.h;

    const dstX0 = srcX0 + Math.round(destOffset.dx);
    const dstY0 = srcY0 + Math.round(destOffset.dy);

    const sampleRadius = Math.max(5, Math.floor(Math.min(srcW, srcH) * 0.3));

    let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

    for (let sy = 0; sy < srcH; sy++) {
      for (let sx = 0; sx < srcW; sx++) {
        const dx = dstX0 + sx;
        const dy = dstY0 + sy;
        if (dx < 0 || dx >= w || dy < 0 || dy >= h) continue;

        const srcPx = srcX0 + sx;
        const srcPy = srcY0 + sy;
        if (srcPx < 0 || srcPx >= w || srcPy < 0 || srcPy >= h) continue;

        const dstIdx = (dy * w + dx) * 4;
        const srcIdx = (srcPy * w + srcPx) * 4;

        if (layer.lockTransparency && data[dstIdx + 3] === 0) continue;

        const srcR = srcData[srcIdx]!;
        const srcG = srcData[srcIdx + 1]!;
        const srcB = srcData[srcIdx + 2]!;

        const destAvg = this.getLocalAverage(data, w, h, dx, dy, sampleRadius);
        const srcAvg = this.getLocalAverage(srcData, w, h, srcPx, srcPy, sampleRadius);

        const diffR = destAvg.r - srcAvg.r;
        const diffG = destAvg.g - srcAvg.g;
        const diffB = destAvg.b - srcAvg.b;

        const healedR = clampByte(srcR + diffR);
        const healedG = clampByte(srcG + diffG);
        const healedB = clampByte(srcB + diffB);

        const blend = 0.8;
        data[dstIdx] = Math.round(data[dstIdx]! * (1 - blend) + healedR * blend);
        data[dstIdx + 1] = Math.round(data[dstIdx + 1]! * (1 - blend) + healedG * blend);
        data[dstIdx + 2] = Math.round(data[dstIdx + 2]! * (1 - blend) + healedB * blend);

        if (dx < touchX0) touchX0 = dx;
        if (dy < touchY0) touchY0 = dy;
        if (dx > touchX1) touchX1 = dx;
        if (dy > touchY1) touchY1 = dy;
      }
    }

    if (touchX1 >= 0) {
      const bbox: BBox = { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
      const after = snapshotRegion(layer, bbox);
      const before = extractRegion(layerSnapshot, w, bbox);
      ctx.emit(new HealStrokeCommand(layer.id, bbox, before, after));
    }

    layer.pixels.markDirty();
  }

  private getLocalAverage(
    data: Uint8ClampedArray,
    w: number,
    h: number,
    cx: number,
    cy: number,
    radius: number,
  ): { r: number; g: number; b: number } {
    let rSum = 0, gSum = 0, bSum = 0, count = 0;
    const r2 = radius * radius;
    const minX = Math.max(0, Math.floor(cx - radius));
    const minY = Math.max(0, Math.floor(cy - radius));
    const maxX = Math.min(w - 1, Math.ceil(cx + radius));
    const maxY = Math.min(h - 1, Math.ceil(cy + radius));

    for (let py = minY; py <= maxY; py++) {
      const dy = py - cy;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px - cx;
        if (dx * dx + dy2 > r2) continue;
        const idx = (py * w + px) * 4;
        const weight = 1 - Math.sqrt(dx * dx + dy2) / (radius + 1);
        rSum += data[idx]! * weight;
        gSum += data[idx + 1]! * weight;
        bSum += data[idx + 2]! * weight;
        count += weight;
      }
    }

    if (count === 0) return { r: 128, g: 128, b: 128 };
    return { r: rSum / count, g: gSum / count, b: bSum / count };
  }

  private reset(): void {
    this.state = "idle";
    this.selection = null;
    this.dragAnchor = null;
    this.dragOffset = null;
    this.active = null;
  }

  /** The rectangular selection currently being sized or armed (doc coords), or null. */
  getSelectionRect(): { x0: number; y0: number; x1: number; y1: number } | null {
    return this.selection;
  }

  /**
   * Live offset of the in-progress drag (only non-null while `state ===
   * "dragging"`), for drawing a ghost preview of the patch destination.
   */
  getDragOffset(): { dx: number; dy: number } | null {
    return this.dragOffset;
  }

  /** True once a selection has been armed and the drag is in progress. */
  isDragging(): boolean {
    return this.state === "dragging";
  }

  isActive(): boolean {
    return this.state !== "idle";
  }
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
}
